jest.mock('../TriggerEvent');

import { Logger } from '@samelogic/steps';
import { mock, MockProxy } from 'jest-mock-extended';
import { TriggerConfig, TriggerEventProps, TriggerEvents } from '../../../models';
import { contextFactory } from '../../__tests__/factories';
import { TriggerEvent, OnEventTriggeredEvent } from '../TriggerEvent';
import { OnTriggered, TriggerManager } from './TriggerManager';

const mockedTriggerEvent = <jest.Mock<TriggerEvent>>TriggerEvent;

beforeEach(() => {
  mockedTriggerEvent.mockClear();
});

type Case = {
  name: string;
  triggerConfigs?: TriggerConfig[];
  /**
   * 0 for no trigger, 1 for trigger
   */
  expectedTriggerCount: 0 | 1;
  /**
   * null trigger is not fired, array matches up with config.events.
   */
  onTriggerEvents: (undefined | OnEventTriggeredEvent<TriggerEvents, unknown>)[];
};

describe('TriggerManager', () => {
  describe.each<Case>([
    {
      name: 'when one event is triggered',
      triggerConfigs: [
        {
          event: 'PageView',
          urlPattern: '/*'
        }
      ],
      onTriggerEvents: [
        {
          condition: true,
          props: {
            event: 'PageView',
            urlPattern: ''
          },
          data: {}
        }
      ],
      expectedTriggerCount: 1
    },
    {
      name: 'when no event is triggered',
      triggerConfigs: [
        {
          event: 'PageView',
          urlPattern: '/*'
        }
      ],
      onTriggerEvents: [],
      expectedTriggerCount: 0
    },
    {
      name: 'when one of many events are triggered',
      triggerConfigs: [
        {
          event: 'PageView',
          urlPattern: '/*'
        },
        {
          event: 'ElementClick',
          cssSelector: '.test'
        }
      ],
      onTriggerEvents: [
        {
          condition: true,
          props: {
            event: 'PageView',
            urlPattern: ''
          },
          data: {}
        }
      ],
      expectedTriggerCount: 0
    },
    {
      name: 'when all of many events are triggered',
      triggerConfigs: [
        {
          id: '1',
          event: 'PageView',
          urlPattern: '/*'
        },
        {
          id: '2',
          event: 'ElementClick',
          cssSelector: '.test'
        }
      ],
      onTriggerEvents: [
        {
          condition: true,
          props: {
            event: 'PageView',
            urlPattern: ''
          },
          data: {}
        },
        {
          condition: true,
          props: {
            event: 'ElementClick',
            cssSelector: '.test'
          },
          data: {}
        }
      ],
      expectedTriggerCount: 1
    },
    {
      name: 'when no trigger is configured',
      onTriggerEvents: [],
      expectedTriggerCount: 1
    },
    {
      name: 'when no trigger events exists',
      triggerConfigs: [],
      onTriggerEvents: [],
      expectedTriggerCount: 1
    }
  ])('$name', ({ triggerConfigs, onTriggerEvents, expectedTriggerCount }) => {
    let triggerContainer: TriggerManager;
    let onTriggerMock: jest.Mock<OnTriggered, []>;

    let loggerMock: MockProxy<Logger>;
    const workflowContext = contextFactory.build();

    let mockTriggerEvents: {
      mockEvent: MockProxy<TriggerEvent>;
      eventData?: OnEventTriggeredEvent<TriggerEventProps, unknown>;
    }[];

    beforeEach(() => {
      loggerMock = mock<Logger>();
      onTriggerMock = jest.fn<OnTriggered, []>();

      mockTriggerEvents = [];

      for (const eventData of onTriggerEvents) {
        const mockEvent = mock<TriggerEvent>();
        mockedTriggerEvent.mockImplementationOnce(() => {
          return mockEvent;
        });
        mockTriggerEvents.push({
          mockEvent: mockEvent,
          eventData: eventData
        });
      }

      triggerContainer = new TriggerManager(workflowContext, loggerMock, triggerConfigs);
      triggerContainer.onTriggered = onTriggerMock;
      triggerContainer.start();

      for (const { mockEvent, eventData } of mockTriggerEvents) {
        if (eventData) {
          mockEvent.onChanged?.(eventData);
        }
      }
    });
    it('should start the event triggers', () => {
      for (const { mockEvent } of mockTriggerEvents) {
        expect(mockEvent.start).toHaveBeenCalled();
      }
    });

    it(`should ${expectedTriggerCount > 0 ? '' : 'not '}execute the trigger`, () => {
      expect(onTriggerMock).toHaveBeenCalledTimes(expectedTriggerCount);
    });
  });
});
